import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { TSupportPagesProps } from '@/console/features/support-pages/support-pages.types';
import { TSupportPage } from '@/reseller/reseller.types';
import { CustomerSupportIcon } from '@/shared/assets/icons/customer-support-icon';
import { DocumentationIcon } from '@/shared/assets/icons/documentation-icon';
import { HelpIcon } from '@/shared/assets/icons/help-icon';
import { SecurityIncidentIcon } from '@/shared/assets/icons/security-incident-icon';
import { ButtonWrapper } from '@/shared/components/ui/button/button-wrapper/button-wrapper';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { NavigationItem } from '@/shared/features/sidebar/components/navigation-item/navigation-item';

// Eagerly import all reseller support pages
const resellerSupportPages = import.meta.glob<{
  SUPPORT_PAGES: Record<TSupportPage, string>;
}>('../../../reseller/*/external-pages/support-pages.constants.ts', {
  eager: true,
});

// Get the reseller support pages based on environment variable
const getResellerSupportPages = (): Record<TSupportPage, string> | null => {
  const resellerName = import.meta.env.VITE_APP_SUPER_TENANT?.toLowerCase();

  if (!resellerName) {
    return null;
  }

  // Find the support pages that matches the reseller name
  const supportPagesEntry = Object.entries(resellerSupportPages).find(
    ([path]) =>
      path.includes(
        `/${resellerName}/external-pages/support-pages.constants.ts`,
      ),
  );

  if (!supportPagesEntry) {
    return null;
  }

  return supportPagesEntry[1].SUPPORT_PAGES;
};

export const SupportPages: React.FC<TSupportPagesProps> = ({ open }) => {
  const { t } = useTranslation();
  const supportPages = getResellerSupportPages();

  const icon: Record<TSupportPage, ReactElement> = {
    documentation: <DocumentationIcon />,
    customer_support: <CustomerSupportIcon />,
    security_incident_reporting: <SecurityIncidentIcon />,
  };

  if (!supportPages) {
    return null;
  }

  return (
    <PopoverController
      trigger={(popoverOpen) => (
        <ButtonWrapper>
          <NavigationItem
            open={open}
            icon={<HelpIcon />}
            name={t('pages:support-faq')}
            clicked={popoverOpen}
          />
        </ButtonWrapper>
      )}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      {() =>
        Object.entries(supportPages)?.map(([page, url]) => (
          <MenuItem
            key={page}
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            icon={icon[page as keyof typeof icon]}
          >
            {t(`pages:${page}`)}
          </MenuItem>
        ))
      }
    </PopoverController>
  );
};
